import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Row,
  Col,
  Button,
  Space,
  Spin,
  Empty,
  message,
  Drawer
} from 'antd';
import {
  ClockCircleOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import { calendarConfigDB, timeSlotsDB } from '../services/firebase/database';
import { useTheme } from './ParticleBackground';
import useResponsive from '../hooks/useResponsive';
import moment from 'moment';

const { Title } = Typography;

const CalendarPicker = ({
  onSlotSelect,
  selectedSlot,
  disabled = false,
  minDate = new Date(),
  maxDate = null
}) => {
  const { theme } = useTheme();
  const { isMobile } = useResponsive();
  const [calendarConfig, setCalendarConfig] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(moment().startOf('month'));
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(selectedSlot ? selectedSlot.date : null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const getThemeStyles = () => {
    if (theme === 'dark') {
      return {
        background: '#0a0a0a',
        cardBg: '#141414',
        cardBorder: 'rgba(255, 255, 255, 0.1)',
        textPrimary: '#ffffff',
        textSecondary: 'rgba(255, 255, 255, 0.45)',
        primaryColor: '#1890ff',
        accentColor: '#eb2f96',
        availableSlot: '#52c41a',
        selectedSlot: '#1890ff',
        disabledSlot: '#8c8c8c',
        calendarHeader: '#1890ff',
        spotLabel: '#eb2f96'
      };
    } else {
      return {
        background: '#ffffff',
        cardBg: '#ffffff',
        cardBorder: '#f0f0f0',
        textPrimary: '#262626',
        textSecondary: '#8c8c8c',
        primaryColor: '#1890ff',
        accentColor: '#eb2f96',
        availableSlot: '#52c41a',
        selectedSlot: '#1890ff',
        disabledSlot: '#8c8c8c',
        calendarHeader: '#1890ff',
        spotLabel: '#eb2f96'
      };
    }
  };

  const themeStyles = getThemeStyles();

  const loadCalendarConfig = async () => {
    try {
      const config = await calendarConfigDB.getConfig();
      setCalendarConfig(config);
    } catch (error) {
      console.error('Error loading calendar config:', error);
      message.error('Failed to load calendar configuration');
    }
  };

  const loadAvailableSlots = useCallback(async () => {
    if (!calendarConfig) return;

    try {
      setLoading(true);
      const startDate = currentMonth.clone().startOf('month').format('YYYY-MM-DD');
      const endDate = currentMonth.clone().endOf('month').format('YYYY-MM-DD');

      // Load available slots for the current month
      const slots = await timeSlotsDB.getAvailableSlots(startDate, endDate);

      // Filter out past slots
      const now = moment();
      const futureSlots = slots.filter(slot => {
        const slotDateTime = moment(`${slot.date} ${slot.time}`, 'YYYY-MM-DD HH:mm');
        return slotDateTime.isAfter(now);
      });

      // Group slots by date
      const slotsByDate = futureSlots.reduce((acc, slot) => {
        if (!acc[slot.date]) {
          acc[slot.date] = [];
        }
        acc[slot.date].push(slot);
        return acc;
      }, {});

      setAvailableSlots(slotsByDate);
    } catch (error) {
      console.error('Error loading available slots:', error);
      message.error(`Failed to load available time slots: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [currentMonth, calendarConfig]);

  useEffect(() => {
    loadCalendarConfig();
  }, []);

  useEffect(() => {
    if (calendarConfig) {
      loadAvailableSlots();
    }
  }, [currentMonth, calendarConfig, loadAvailableSlots]);

  const generateMonthDays = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startDay = startOfMonth.day(); // 0 for Sunday
    const totalDays = endOfMonth.date();

    const days = [];

    // Previous month's trailing days
    const prevMonth = currentMonth.clone().subtract(1, 'month');
    const prevMonthEnd = prevMonth.clone().endOf('month').date();
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: prevMonth.clone().date(prevMonthEnd - i),
        isCurrentMonth: false
      });
    }

    // Current month's days
    for (let i = 1; i <= totalDays; i++) {
      days.push({
        date: currentMonth.clone().date(i),
        isCurrentMonth: true
      });
    }

    // Next month's leading days
    const remaining = 42 - days.length; // 6 rows of 7 days
    for (let i = 1; i <= remaining; i++) {
      days.push({
        date: currentMonth.clone().add(1, 'month').date(i),
        isCurrentMonth: false
      });
    }

    return days;
  };

  const isDateDisabled = (date) => {
    if (disabled) return true;

    // Check if it's a business day
    const dayName = date.format('dddd').toLowerCase();

    // Check if it's a business day
    if (!calendarConfig?.businessHours[dayName]?.enabled) {
      return true;
    }

    // Check if it's in the past
    if (date.isBefore(moment(), 'day')) {
      return true;
    }

    // Check if it's beyond max advance booking days
    if (calendarConfig?.advanceBookingDays) {
      const maxDate = moment().add(calendarConfig.advanceBookingDays, 'days');
      if (date.isAfter(maxDate, 'day')) {
        return true;
      }
    }

    return false;
  };

  const handleSlotClick = (date, time) => {
    if (disabled) return;

    const slotInfo = {
      date: date,
      time: time,
      dateTime: moment(`${date} ${time}`, 'YYYY-MM-DD HH:mm')
    };

    setSelectedDate(date);
    onSlotSelect(slotInfo);
  };

  const monthDays = generateMonthDays();
  const selectedDaySlots = selectedDate ? (availableSlots[selectedDate] || []) : [];

  return (
    <div style={{
      background: themeStyles.background,
      borderRadius: '16px',
      padding: '24px',
      border: `1px dashed ${themeStyles.cardBorder}`,
      maxWidth: '1000px',
      margin: '0 auto'
    }}>
      <Row gutter={[32, 32]}>
        {/* Left Column: Calendar */}
        <Col xs={24} md={14}>
          <div style={{ padding: '8px' }}>
            <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
              <Col>
                <Title level={4} style={{
                  color: themeStyles.calendarHeader,
                  margin: 0,
                  fontSize: '24px',
                  fontWeight: '400'
                }}>
                  {currentMonth.format('MMMM YYYY')}
                </Title>
              </Col>
              <Col>
                <Space size="large">
                  <Button
                    type="text"
                    icon={<LeftOutlined style={{ color: '#bae7ff' }} />}
                    onClick={() => setCurrentMonth(prev => prev.clone().subtract(1, 'month'))}
                    disabled={currentMonth.isSameOrBefore(moment(), 'month')}
                  />
                  <Button
                    type="text"
                    icon={<RightOutlined style={{ color: themeStyles.calendarHeader }} />}
                    onClick={() => setCurrentMonth(prev => prev.clone().add(1, 'month'))}
                  />
                </Space>
              </Col>
            </Row>

            {/* Calendar Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', textAlign: 'center' }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                <div key={day} style={{
                  padding: '12px 0',
                  color: themeStyles.textPrimary,
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {day}
                </div>
              ))}

              {monthDays.map((dayObj, index) => {
                const dateStr = dayObj.date.format('YYYY-MM-DD');
                const daySlots = availableSlots[dateStr] || [];
                const isDisabled = !dayObj.isCurrentMonth || isDateDisabled(dayObj.date);
                const isSelected = selectedDate === dateStr;
                const spotCount = daySlots.length;

                return (
                  <div
                    key={index}
                    style={{
                      padding: '12px 0',
                      cursor: isDisabled ? 'default' : 'pointer',
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      opacity: dayObj.isCurrentMonth ? 1 : 0.3
                    }}
                    onClick={() => {
                      if (!isDisabled) {
                        setSelectedDate(dateStr);
                        if (isMobile && daySlots.length > 0) {
                          setDrawerVisible(true);
                        }
                      }
                    }}
                  >
                    <div style={{
                      width: '36px',
                      height: '36px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      background: isSelected ? themeStyles.selectedSlot : 'transparent',
                      color: isSelected ? 'white' : (isDisabled ? themeStyles.disabledSlot : themeStyles.textPrimary),
                      fontSize: '16px',
                      marginBottom: '4px',
                      transition: 'all 0.2s'
                    }}>
                      {dayObj.date.date()}
                    </div>
                    {dayObj.isCurrentMonth && !isDisabled && spotCount > 0 && (
                      <div style={{
                        fontSize: '10px',
                        color: themeStyles.spotLabel,
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {spotCount} SPOTS
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Col>

        {/* Right Column: Time Slots - Desktop only */}
        {!isMobile && (
          <Col xs={24} md={10}>
            <div style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center'
            }}>
              {loading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                  <Spin size="large" />
                </div>
              ) : selectedDate ? (
                <div>
                  {selectedDaySlots.length > 0 ? (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '12px'
                    }}>
                      {selectedDaySlots.map((slot, index) => {
                        const isSlotSelected = selectedSlot &&
                          selectedSlot.date === slot.date &&
                          selectedSlot.time === slot.time;

                        return (
                          <div
                            key={index}
                            onClick={() => handleSlotClick(slot.date, slot.time)}
                            style={{
                              padding: '12px 8px',
                              textAlign: 'center',
                              borderRadius: '24px',
                              cursor: 'pointer',
                              background: isSlotSelected ? themeStyles.selectedSlot : 'transparent',
                              color: isSlotSelected ? 'white' : (isSlotSelected ? themeStyles.textPrimary : themeStyles.textSecondary),
                              fontSize: '14px',
                              fontWeight: isSlotSelected ? '600' : '400',
                              transition: 'all 0.2s',
                            }}
                          >
                            {moment(slot.time, 'HH:mm').format('h:mm A')}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <Empty description="No slots available for this date" />
                  )}
                </div>
              ) : (
                <div style={{ textAlign: 'center', color: themeStyles.textSecondary }}>
                  <ClockCircleOutlined style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.2 }} />
                  <p>Please select a date to view available time slots</p>
                </div>
              )}
            </div>
          </Col>
        )}
      </Row>

      {/* Mobile Time Slots Drawer */}
      <Drawer
        title={`Available Times - ${selectedDate ? moment(selectedDate).format('MMM DD, YYYY') : ''}`}
        placement="bottom"
        height="60vh"
        open={isMobile && drawerVisible}
        onClose={() => setDrawerVisible(false)}
        styles={{
          body: {
            background: themeStyles.background,
            padding: '24px'
          },
          header: {
            background: themeStyles.background,
            borderBottom: `1px solid ${themeStyles.cardBorder}`
          }
        }}
      >
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Spin size="large" />
          </div>
        ) : selectedDaySlots.length > 0 ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: '12px'
          }}>
            {selectedDaySlots.map((slot, index) => {
              const isSlotSelected = selectedSlot &&
                selectedSlot.date === slot.date &&
                selectedSlot.time === slot.time;

              return (
                <div
                  key={index}
                  onClick={() => {
                    handleSlotClick(slot.date, slot.time);
                    setDrawerVisible(false);
                  }}
                  style={{
                    padding: '16px 8px',
                    textAlign: 'center',
                    borderRadius: '24px',
                    cursor: 'pointer',
                    background: isSlotSelected ? themeStyles.selectedSlot : 'transparent',
                    border: `1px solid ${isSlotSelected ? themeStyles.selectedSlot : themeStyles.cardBorder}`,
                    color: isSlotSelected ? 'white' : themeStyles.textPrimary,
                    fontSize: '15px',
                    fontWeight: isSlotSelected ? '600' : '400',
                    transition: 'all 0.2s',
                  }}
                >
                  {moment(slot.time, 'HH:mm').format('h:mm A')}
                </div>
              );
            })}
          </div>
        ) : (
          <Empty description="No slots available for this date" />
        )}
      </Drawer>
    </div>
  );
};

export default CalendarPicker;